import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { authApi, companyApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  PageHeader,
  StatTile,
  useToast,
} from '@/components/ui';
import { CloseCompanyCard } from '@/features/access/CloseCompanyCard';
import { SignInName } from '@/features/auth/SignInName';
import { ACCESS_KEYS, groupCatalogue, roleLabel } from '@/features/access/roles';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

/**
 * The signed-in member's own account: their password, and what they can and
 * cannot do in this workspace, with whether each permission came with their
 * role or was set for them alone. The API enforces the same set, so the
 * permission list is an explanation, not a gate.
 */
export function CompanyAccessPage() {
  const { user } = useAuth();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ACCESS_KEYS.mine,
    queryFn: companyApi.getMyAccess,
  });

  // The password card renders whatever happens to the permissions request:
  // changing a password needs no verified email, reading access does.
  return (
    <div className="space-y-6">
      <PageHeader
        title="My account"
        description={`Your password, and what you can do in ${user?.company?.company_name ?? 'this workspace'}. Admins set your access; it updates as soon as they change it.`}
      />

      <PasswordCard />

      {isPending ? (
        <LoadingState label="Loading your access…" />
      ) : isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : (
        <AccessDetails access={data} />
      )}

      {/* The owner alone can close the company; the API checks it too. */}
      {data?.is_owner && user?.company?.company_name && (
        <CloseCompanyCard companyName={user.company.company_name} />
      )}
    </div>
  );
}

function AccessDetails({ access: data }) {
  const held = new Set(data.permissions);
  const granted = new Set(data.granted);
  const revoked = new Set(data.revoked);
  const groups = groupCatalogue(data.catalogue);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Role" value={roleLabel(data.role)} />
        <StatTile label="Permissions" value={`${held.size} of ${data.catalogue.length}`} />
        <StatTile
          label="Set just for you"
          value={data.locked ? '—' : String(granted.size + revoked.size)}
        />
      </div>

      {data.locked && (
        <Alert tone="info" title="Full access">
          {data.is_owner ? 'As the workspace owner' : 'As an admin'} you hold every permission,
          including managing the team and its roles.
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Permissions"
          description="Ticked items are what the API lets you do. Ask an admin if something you need is missing."
        />
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((group) => (
            <div key={group.group} className="min-w-0">
              <p className="text-ink mb-2 text-sm font-semibold">{group.group}</p>
              <ul className="space-y-2">
                {group.items.map((entry) => {
                  const has = held.has(entry.code);
                  return (
                    <li key={entry.code} className="flex items-start gap-2 text-sm">
                      <Icon
                        name={has ? 'check' : 'x'}
                        size={15}
                        className={cn(
                          'mt-0.5 shrink-0',
                          has ? 'text-success-600' : 'text-ink-muted',
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={has ? 'text-ink' : 'text-ink-muted'}>
                          {entry.description}
                        </span>
                        {granted.has(entry.code) && (
                          <Badge tone="success" className="ml-2">
                            Added for you
                          </Badge>
                        )}
                        {revoked.has(entry.code) && (
                          <Badge tone="danger" className="ml-2">
                            Removed for you
                          </Badge>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

/** Invited members arrive with a temporary password; this is where it goes. */
function PasswordCard() {
  const toast = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword({ new_password: newPassword }),
    onSuccess: () => {
      toast.success('Password changed. Other sessions were signed out.');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not change the password')),
  });

  return (
    <Card>
      <CardHeader
        title="Password"
        description="Signed in with a temporary password from your invitation? Replace it here. Changing it signs out every other session."
      />
      <SignInName />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          hint="At least 8 characters, with upper and lower case and a number."
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={mismatch ? 'The two passwords do not match.' : undefined}
        />
      </div>
      <div className="mt-6 flex justify-end">
        <Button
          isLoading={changePassword.isPending}
          disabled={newPassword.length < 8 || confirmPassword.length === 0 || mismatch}
          onClick={() => changePassword.mutate()}
        >
          Change password
        </Button>
      </div>
    </Card>
  );
}
